import type { OnDestroy, OnInit } from '@angular/core';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import isEqual from 'lodash/isEqual';
import { map } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import format from 'date-fns/format';
import { Logger } from '@shared/common/logger';
import { slideY } from '@studiobuki/web-core';
import { InputComponent } from '@studiobuki/web-core/lib/input';
import { SHIPPING_METHOD_REGULAR } from '@shared/common/shipping/constants';
import Subscriber from '@shared/common/subscriber';
import { MediaService } from '@studiobuki/web-core/lib/media';
import { ProductionService } from '@studiobuki/web-core/lib/production';
import { EFormPaymentType } from '@shared/jp/interfaces';
import { TRegion } from '@shared/jp/shipping/interfaces';
import type { IShipping as IShippingFE } from '@shared/jp/data/shipping';
import type { TFormCheckoutData } from './form-checkout.interfaces';

const log = new Logger('FormCheckoutComponent');

@Component({
  selector: 'app-form-checkout',
  templateUrl: './form-checkout.component.html',
  styleUrls: ['./form-checkout.component.scss'],
  // * commented because of accordion detection issues
  // changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideY()],
})
export class FormCheckoutComponent implements OnInit, OnDestroy {
  @ViewChild('inputCard') inputCardComponent!: InputComponent;

  @Input() isGift?: boolean;

  @Input() set region(r: TRegion) {
    this.region$.next(r);
  }

  /** `undefined` when invalid */
  @Input() set data(data: TFormCheckoutData | undefined) {
    if (!isEqual(data, this.data)) {
      if (data) {
        this.type$.next(data.type);
      }
    }
  }

  get data() {
    const type = this.type$.value;

    return {
      type,
    };
  }

  public type$ = new BehaviorSubject<EFormPaymentType>(EFormPaymentType.card);

  @Output() dataChange = this.type$.pipe(
    map((type) => ({
      type,
    })),
  );

  @Input() submitButtonDisabled: boolean = false;

  @Output() submitButtonClick = new EventEmitter<void>();

  @Input() set shipping(value: IShippingFE | undefined) {
    if (value) {
      this.shipping$.next(value);
    }
  }

  @Input() footerDescription?: string | undefined | null;

  public shipping$ = new ReplaySubject<IShippingFE>(1);

  public readonly EFormPaymentType = EFormPaymentType;

  public readonly region$ = new ReplaySubject<TRegion>(1);

  public readonly expectedArrivalDate$ = this._productionService
    .getExpectedArrivalDate$(
      this.shipping$.pipe(map((_) => _.id)),
      this.region$.pipe(map((r) => r.nameEnglish)),
    )
    .pipe(map(([date]) => format(date, 'yyyy/MM/dd')));

  public readonly endOfCycleDate$ =
    this._productionService.endOfCycleDate$.pipe(
      map((date) => format(date, 'yyyy/MM/dd')),
    );

  public readonly nextExpectedArrivalDate$ = this._productionService
    .getExpectedArrivalDate$(
      this.shipping$.pipe(map((_) => _.id)),
      this.region$.pipe(map((r) => r.nameEnglish)),
      this._productionService.nextExpectedShippingDate$,
    )
    .pipe(map(([date]) => format(date, 'yyyy/MM/dd')));

  private readonly _sub = new Subscriber();

  public readonly SHIPPING_METHOD_REGULAR = SHIPPING_METHOD_REGULAR;

  constructor(
    public mediaService: MediaService,
    private _productionService: ProductionService,
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  public setType(type: EFormPaymentType) {
    const { type$ } = this;

    if (type$.value === type) return;

    type$.next(type);
  }
}
